import React, { useState } from 'react';

import { EditingMode, PermissionRole } from '@aline/common';
import { ListItemIcon, ListItemText, MenuItem, Typography } from '@mui/material';
import { capitalCase } from 'change-case';
import { AlineTooltip, ToolbarSelect } from 'src/components';
import { CommentingIcon } from 'src/components/icons/CommentingIcon';
import { EditingIcon } from 'src/components/icons/EditingIcon';
import { SuggestingIcon } from 'src/components/icons/SuggestingIcon';
import { ViewingIcon } from 'src/components/icons/ViewingIcon';
import { useEditorTabStateActions } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { editingModeEnabledForUser } from 'src/helpers';
import { useGetEditorUserState } from 'src/hooks';
import { useEditor, useEditorLoading } from 'src/layout-engine';
import { useCurrentEditingMode, useCurrentUserPermissionRole } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

const useIconStyles = makeStyles()(theme => ({
  icon: {
    marginRight: theme.spacing(1),
  },
}));

function EditingModeIcon({ editingMode }: { editingMode: EditingMode }): JSX.Element {
  const { classes } = useIconStyles();

  switch (editingMode) {
    case EditingMode.EDITING:
      return <EditingIcon className={classes.icon} />;
    case EditingMode.SUGGESTING:
      return <SuggestingIcon classes={{ root: classes.icon }} />;
    case EditingMode.COMMENTING:
      return <CommentingIcon className={classes.icon} />;
    case EditingMode.VIEWING:
      return <ViewingIcon className={classes.icon} />;
  }
}

const useStyles = makeStyles<{ editorLoading: boolean }>()((theme, { editorLoading }) => ({
  gutters: {
    paddingLeft: 12,
    paddingRight: 12,
  },
  horizontalDivider: {
    backgroundColor: theme.palette.divider,
  },
  listIcon: {
    minWidth: theme.spacing(4),
  },
  root: {
    fontSize: 14,
  },
  select: {
    height: 24,
    marginRight: theme.spacing(1),
  },
  selectValue: {
    alignItems: 'center',
    color: editorLoading ? theme.palette.action.disabled : undefined,
    display: 'flex',
    marginRight: theme.spacing(1),
  },
}));

export const EditingModeSelect = React.memo(function EditingModeSelect(): JSX.Element | null {
  const editor = useEditor();
  const {
    documentUi: { setEditingMode },
  } = useEditorTabStateActions();
  const currentUserId = useCurrentUserId();
  const editorLoading = useEditorLoading();
  const editingMode = useCurrentEditingMode();
  const { classes } = useStyles({ editorLoading });
  const { isReadOnly } = useGetEditorUserState();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const { permissionRole } = useCurrentUserPermissionRole();
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const adjustedPermissionRole = isReadOnly ? PermissionRole.Viewer : permissionRole;

  function handleOpen(): void {
    setTooltipOpen(false);
    setMenuOpen(true);
  }

  function handleClose(): void {
    setMenuOpen(false);
  }

  function handleTooltipOpen(): void {
    setTooltipOpen(true);
  }

  function handleTooltipClose(): void {
    setTooltipOpen(false);
  }

  function handleSelectChange(
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { name: string; value: unknown } })
  ): void {
    if (!currentUserId) return;

    if (getIsEditingMode(event.target.value)) setEditingMode(event.target.value);
    ClientTransforms.focus(editor);
  }

  if (!editingMode) return null;

  return (
    <AlineTooltip
      arrow
      disableHoverListener
      open={tooltipOpen && !menuOpen}
      placement="bottom"
      title="Editing mode"
      onClose={handleTooltipClose}
      onOpen={handleTooltipOpen}
    >
      <ToolbarSelect
        MenuProps={{
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          transformOrigin: { horizontal: 'right', vertical: 'top' },
        }}
        className={classes.select}
        disabled={editorLoading}
        menuAttribute="editing-mode-menu"
        open={menuOpen}
        renderValue={(value: unknown) => {
          return (
            <div className={classes.selectValue}>
              <EditingModeIcon editingMode={value as EditingMode} />
              <Typography color="inherit">{capitalCase(value as EditingMode)}</Typography>
            </div>
          );
        }}
        selectAttribute="editing-mode-select"
        selectStyles={{ paddingLeft: 8 }}
        spanAttribute="editing-mode-select-span"
        sx={{
          '& .MuiSelect-select': {
            paddingBottom: '0px !important',
            paddingTop: '0px !important',
          },
        }}
        value={editingMode}
        onClose={handleClose}
        onMouseEnter={(): void => {
          setTooltipOpen(true);
        }}
        onMouseLeave={(): void => {
          setTooltipOpen(false);
        }}
        onOpen={handleOpen}
        onSelectChange={handleSelectChange}
      >
        {[
          EditingMode.EDITING,
          EditingMode.SUGGESTING,
          EditingMode.COMMENTING,
          EditingMode.VIEWING,
        ].map((mode, index) => (
          <MenuItem
            key={index}
            dense
            classes={{
              gutters: classes.gutters,
              root: classes.root,
            }}
            data-cy={mode.toLowerCase()}
            disabled={!editingModeEnabledForUser(mode, adjustedPermissionRole)}
            value={mode}
          >
            <ListItemIcon className={classes.listIcon}>
              <EditingModeIcon editingMode={mode} />
            </ListItemIcon>
            <ListItemText primary={capitalCase(mode)} />
          </MenuItem>
        ))}
      </ToolbarSelect>
    </AlineTooltip>
  );
});

function getIsEditingMode(value: unknown): value is EditingMode {
  return (
    value === EditingMode.EDITING ||
    value === EditingMode.SUGGESTING ||
    value === EditingMode.COMMENTING ||
    value === EditingMode.VIEWING
  );
}
